import type { InternalRequest } from '../client.js';

type Params = Record<string, string | number | boolean | undefined | null>;

function qsFrom(params?: Params): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, String(v));
  return `?${usp}`;
}

export function workEndpoints(req: InternalRequest) {
  return {
    list: (params?: Params) => req.request<unknown[]>(`/work${qsFrom(params)}`),
    get: (id: string) => req.request<unknown>(`/work/${id}`),
    create: (data: Record<string, unknown>) =>
      req.request<unknown>('/work', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Record<string, unknown>) =>
      req.request<unknown>(`/work/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) =>
      req.request<void>(`/work/${id}`, { method: 'DELETE' }),

    /** PATCH /work/:id/status — transition the 7-state work status machine. */
    setStatus: (
      id: string,
      body: { status: string; failureReason?: string },
      opts?: { signal?: AbortSignal },
    ) =>
      req.request<unknown>(`/work/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        signal: opts?.signal,
      }),
    setShare: (id: string, body: Record<string, unknown>) =>
      req.request<unknown>(`/work/${id}/share`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    optimize: (id: string) =>
      req.request<unknown>(`/work/${id}/optimize`, { method: 'POST' }),

    /** Public — share view by token, no auth required. */
    share: (token: string) => req.request<unknown>(`/work/share/${token}`),

    stops: {
      list: (workId: string) => req.request<unknown[]>(`/work/${workId}/stops`),
      add: (workId: string, data: Record<string, unknown>) =>
        req.request<unknown>(`/work/${workId}/stops`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      update: (workId: string, stopId: string, data: Record<string, unknown>) =>
        req.request<unknown>(`/work/${workId}/stops/${stopId}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),
      setStatus: (workId: string, stopId: string, body: Record<string, unknown>) =>
        req.request<unknown>(`/work/${workId}/stops/${stopId}/status`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      /** POST /work/:id/stops/:stop_id/reopen — undo a server auto-completion. */
      reopen: (workId: string, stopId: string) =>
        req.request<{ stop: unknown }>(`/work/${workId}/stops/${stopId}/reopen`, { method: 'POST' }),
      delete: (workId: string, stopId: string) =>
        req.request<void>(`/work/${workId}/stops/${stopId}`, { method: 'DELETE' }),
      reorder: (workId: string, orderedStopIds: string[]) =>
        req.request<unknown>(`/work/${workId}/stops/reorder`, {
          method: 'PATCH',
          body: JSON.stringify({ stopIds: orderedStopIds }),
        }),
    },
  };
}
